import type { CSSProperties } from "react";
import type { RendererProps } from "../core/element-registry";
import { useRenderContext } from "../core/render-context";
import { useAtomDefaults } from "../contexts/atom-defaults-context";
import { Progress } from "../components/ui/progress";
import { cn } from "../lib/utils";
import { finiteNumber } from "./property-value-utils";

export function ProgressRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext();
  // Theme-v2 atom defaults: theme-variant/size surfaced on data-* attrs only.
  // Note: legacy progress blocks overload `element.variant` as a numeric
  // value carrier (see `rawSource = element.value ?? element.variant`
  // below); that semantic is preserved and is separate from the theme-v2
  // data-variant contract. The theme-variant flows from atom defaults
  // only — there is no "explicit element.variant wins" case for progress.
  const atomDefaults = useAtomDefaults("progress");
  const resolvedVariant = atomDefaults.defaultVariant ?? "default";
  const resolvedSize =
    (element as { size?: string }).size ??
    atomDefaults.defaultSize ??
    "default";

  // Read value from element.value (preferred) or element.variant (legacy)
  const rawSource = element.value ?? element.variant ?? "0";
  const rawValue = String(resolveSymbol(String(rawSource)) ?? "0");
  const rawMax =
    element.max !== undefined ? resolveSymbol(String(element.max)) : 100;
  const max = Math.max(1, finiteNumber(rawMax, 100));
  const value = Math.max(0, Math.min(max, Number(rawValue) || 0));

  return (
    <Progress
      value={value}
      max={max}
      className={cn(element.className)}
      data-testid={`progress-${element.id}`}
      data-atom-type="progress"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-value={String(value)}
      data-max={String(max)}
    />
  );
}

export function ProgressIndicatorRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext();
  const rawValue =
    element.value !== undefined ? resolveSymbol(String(element.value)) : 0;
  const rawMax =
    element.max !== undefined ? resolveSymbol(String(element.max)) : 100;
  const max = Math.max(1, finiteNumber(rawMax, 100));
  const value = Math.max(0, Math.min(max, finiteNumber(rawValue, 0)));
  const percentage = (value / max) * 100;
  const authoredStyle =
    element.style && typeof element.style === "object"
      ? (element.style as CSSProperties)
      : undefined;
  const inheritsTrackRadius =
    authoredStyle?.borderRadius == null &&
    !/(?:^|\s)rounded(?:-|\s|$)/.test(element.className ?? "");

  return (
    <div
      role="progressbar"
      aria-label={element.ariaLabel ?? "Progress"}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      className={cn(
        "cn-progress-indicator h-full transition-all",
        element.className,
      )}
      style={{
        ...(inheritsTrackRadius ? { borderRadius: "inherit" } : {}),
        ...authoredStyle,
        width: `${percentage}%`,
      }}
      data-slot="progress-indicator"
      data-testid={`progress-indicator-${element.id}`}
      data-atom-type="progress-indicator"
      data-value={String(value)}
      data-max={String(max)}
    />
  );
}
